import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fieldset } from '@clay-run/glaze/fieldset';
import { InputField } from '@clay-run/glaze/input-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { SwitchField } from '@clay-run/glaze/switch-field';
import { ComboboxField } from '@clay-run/glaze/combobox-field';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { Button } from '@clay-run/glaze/button';
import { TextLink } from '@clay-run/glaze/textlink';
import { Alert, AlertDescription } from '@clay-run/glaze/alert';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Card } from '@clay-run/glaze/card';
import { CollapsibleCard, CollapsibleCardBody, CollapsibleCardHeader } from '@clay-run/glaze/collapsible-card';
import { Image } from '@clay-run/glaze/image';
import { Heading } from '@clay-run/glaze/heading';
import { Text } from '@clay-run/glaze/text';
// `/toast` has no flat `dist/toast.d.ts` — see index.tsx's import comment.
import { addApiErrorToast } from '../../_shared/api-error-toast';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { memberPrefillFields, type CampaignMember } from '../campaign-members';
import {
  LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
  campaignWhenAndWhere,
  fetchCampaigns,
  type Campaign,
} from '../campaigns';
import {
  resetSubmitStatusForRetry,
  seedScanIntoScansCaches,
  useScanApi,
  useSubmitStatusQuery,
} from '../hooks/useScanApi';
import { normalizeLinkedInInput } from '../linkedin-qr';
import { hasRequiredFieldErrors, validateRequiredScanFields, type RequiredFieldErrors } from '../submit-validation';
import {
  PARTIAL_DETECTION_MESSAGE,
  SCAN_STATUS_BADGE,
  SCAN_STATUS_LABEL,
  SUBMIT_THROTTLED_MESSAGE,
  scanTitle,
  type Scan,
  type ScanSubmitResult,
} from '../scan';
import type { EnrichLinkedInResponse } from '../enrich-clay';
import {
  computeEnrichmentUpdates,
  needsReviewPrefill,
  resolveEnrichedInfoState,
  type AiField,
} from '../scan-review-logic';
import EnrichedInfoSection from './EnrichedInfoSection';
import EnrichingWithClay from './EnrichingWithClay';
import { sfdcUsersQueryOptions } from '../rep-mapping-client';
import type { Rep } from '../rep-mapping-wire';
import CampaignMemberSearch from './CampaignMemberSearch';

/**
 * The scan review form (R10/R11/R12/R14, U6 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md) — replaces
 * `ViewSwitcher.tsx`'s `ActiveScanPreview` for every non-`processing` scan
 * (that component still owns the `processing` skeleton state; U4/U5's
 * status-badge/title/source-line helpers are reused here unchanged).
 *
 * Copy is verbatim from docs/lead-scanner-prototype.html's `scanForm()` /
 * `formTail()` / `memberSearchBlock()` (~lines 1230-1420) per the
 * prototype-copy-fidelity rule.
 *
 * The whole form is intentionally remounted (via the `key={scan.id}` the
 * caller passes) whenever the active scan changes — see the file-level note
 * on `initial*` below for why that is the right tool here instead of a
 * reseed effect.
 */

function initialAiFields(scan: Scan): Set<AiField> {
  // Photo intake is the only path that ever machine-populates a field
  // (vision extraction → name/company/email; QR decode → linkedin only,
  // Key Technical Decision 4) — a manual draft's fields are always
  // rep-typed, never AI-populated (R10).
  if (scan.sourceType !== 'photo') return new Set();
  const fields = new Set<AiField>();
  if (scan.name) fields.add('name');
  if (scan.company) fields.add('company');
  if (scan.email) fields.add('email');
  if (scan.linkedin) fields.add('linkedin');
  return fields;
}

interface ScanReviewProps {
  scan: Scan;
}

export default function ScanReview({ scan }: ScanReviewProps) {
  const { apiFetch } = useApi();
  const { user } = useCurrentUser();
  const scanApi = useScanApi();
  const queryClient = useQueryClient();

  // ─── Form state, seeded once from `scan` at mount. The caller keys this
  // component by `scan.id`, so switching to a different scan remounts it
  // entirely rather than reseeding an already-mounted form mid-edit (which
  // would either blow away in-progress edits on an unrelated refetch, or —
  // worse — silently merge one scan's edits onto another's row). ───────────

  const [name, setName] = useState(scan.name);
  const [company, setCompany] = useState(scan.company);
  // `needs_review` pre-fill (fill-if-blank only, `needsReviewPrefill` above):
  // the Clay routine may have resolved an email/LinkedIn even on a partial/
  // failed run — persisted onto the scan row as `enrichedEmail`/
  // `enrichedLinkedin` by `finalizeSubmittingScan`. Only ever fills a BLANK
  // field; a rep-typed or AI-extracted value already on the row always wins.
  // Seeded here from `scan` at mount; the effect below (Fix 5) re-applies the
  // SAME fill-if-blank logic on a LIVE prop update, since `ActiveScanPanel.tsx`
  // keys this component by `scan.id` and a row flipping `submitting` →
  // `needs_review` while the SAME scan stays open re-renders rather than
  // remounts — this initializer alone would never see that transition.
  const prefill = needsReviewPrefill(scan);
  const [email, setEmail] = useState(scan.email || prefill.email || '');
  const [linkedin, setLinkedin] = useState(scan.linkedin || prefill.linkedin || '');
  const [notes, setNotes] = useState(scan.notes);
  const [routeToGtme, setRouteToGtme] = useState(scan.routeToGtme);
  const [suppressFollowUp, setSuppressFollowUp] = useState(scan.suppressFollowUp);
  const [aiFields, setAiFields] = useState<Set<AiField>>(() => initialAiFields(scan));
  const [fieldErrors, setFieldErrors] = useState<RequiredFieldErrors>({ name: null, company: null });

  // Part 2: prefer the previously-resolved SFDC Contact over a blank
  // `matchedContactId` — a forced resubmit ("Update in Salesforce") that
  // re-runs the routine with no contact id would risk creating a duplicate
  // Contact.
  const [matchedContactId, setMatchedContactId] = useState<string | null>(
    scan.matchedContactId ?? scan.sfdcContactId,
  );
  const [matchedMember, setMatchedMember] = useState<CampaignMember | null>(null);

  const [campaignId, setCampaignId] = useState(scan.campaignId);
  const [scannedByEmail, setScannedByEmail] = useState(scan.scannedByEmail);
  const [campaignQuery, setCampaignQuery] = useState('');
  const [repQuery, setRepQuery] = useState('');
  const [initialCampaignId] = useState(scan.campaignId);
  const [initialScannedByEmail] = useState(scan.scannedByEmail);

  // The optimistic seed a FIRED submission puts on screen (U10): the submit
  // call itself has no outcome to report, so the "Enriched info" section
  // shows the pending arm on `{pending: true}` and the poll below is what
  // ever replaces it. `null` means no submission has been fired from this
  // mounted form yet — `resolveEnrichedInfoState` (`scan-review-logic.ts`)
  // is what decides whether that means "nothing to show" or "fall back to
  // the scan's own persisted result" for a scan reopened later.
  const [firedSeed, setFiredSeed] = useState<ScanSubmitResult | null>(null);

  // Scroll target for the top-of-page "View results" jump button — only
  // rendered when `resolveEnrichedInfoState` says the section is showing a
  // PERSISTED result (a scan reopened later with nothing in flight); a live
  // in-page result needs no jump button, the rep is already looking at it.
  const enrichedInfoRef = useRef<HTMLDivElement>(null);

  // ─── "View Image" accordion — rendered inline, no separate viewer. Fetched
  // via `apiFetch` and rendered from a blob URL rather than a bare
  // `<img src>` or a new tab, because this app authenticates via an
  // `Authorization` header (Clerk token), which neither an `<img>` tag nor a
  // plain navigation can set. `enabled` gates
  // the fetch to only fire once the rep actually expands the accordion, and
  // only for a scan that has a photo at all. ──────────────────────────────
  const [photoAccordionOpen, setPhotoAccordionOpen] = useState(false);

  const photoQuery = useQuery({
    queryKey: ['lead-scanner', 'scan-photo', scan.id],
    enabled: photoAccordionOpen && scan.hasPhoto,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { suppressErrorToast: true },
    queryFn: async () => {
      const res = await apiFetch(`/api/tools/lead-scanner/scans/${scan.id}/photo`);
      return await res.blob();
    },
  });

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    const blob = photoQuery.data;
    if (!blob) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setPhotoUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setPhotoUrl(null);
    };
  }, [photoQuery.data]);

  function clearAiField(field: AiField) {
    setAiFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }

  // Companion to `clearAiField` above — marks fields the LinkedIn-enrichment
  // call (auto or manual, `applyEnrichment` below) actually filled. Stable
  // identity (`useCallback` with no deps: `setAiFields`'s updater form never
  // closes over component state) so it can sit in another `useCallback`'s
  // dependency array without ever forcing a re-run.
  const addAiFields = useCallback((fields: AiField[]) => {
    if (fields.length === 0) return;
    setAiFields((prev) => {
      const next = new Set(prev);
      for (const field of fields) next.add(field);
      return next;
    });
  }, []);

  // Fix 5 (extended for the enrichment-survives-navigation change):
  // re-applies fill-if-blank logic from the LIVE `scan` prop on every render
  // where it changes — covering two cases the mount-time `useState`
  // initializers above cannot, since `ActiveScanPanel.tsx` keys this
  // component by `scan.id` and both transitions RE-RENDER the same mounted
  // form rather than remounting it:
  //
  //   1. (original Fix 5) a scan flipping `submitting` → `needs_review` —
  //      the `prefill.email`/`prefill.linkedin` fill.
  //   2. `EnrichmentManager.tsx` (mounted once at the app root, survives
  //      in-app navigation — `index.tsx`) PATCHing this scan's
  //      name/company/email while its review form happens to still be open.
  //      The auto-fire query that used to live in THIS component now lives
  //      there instead, so this is what makes an already-open form pick up
  //      its result rather than needing a remount to see it.
  //
  // `computeEnrichmentUpdates` (mode `'auto'`) — the SAME fill-if-blank core
  // `applyEnrichment` below uses for the manual "Enrich with Clay" path — is
  // reused for case 2 rather than a bespoke check, which is what marks a
  // newly-filled field via `addAiFields`, identically to any other
  // enrichment fill. It reads the CURRENT `name`/`company`/`email` closure
  // values, which are accurate as of the render this effect was scheduled
  // from; deliberately NOT listed as effect deps (mini-apps has
  // `react/exhaustive-deps` off, `.oxlintrc.json`) so this fires only when
  // the SCAN prop changes, never on every keystroke. The functional
  // `setEmail`/`setLinkedin` form for the prefill values (case 1) reads the
  // CURRENT value at apply time instead, so a rep who started typing during
  // the run is never clobbered either way.
  useEffect(() => {
    const { updates, filled } = computeEnrichmentUpdates(
      { name: scan.name, company: scan.company, email: scan.email },
      { name, company, email },
      'auto',
    );
    if (updates.name !== undefined) setName(updates.name);
    if (updates.company !== undefined) setCompany(updates.company);
    if (updates.email !== undefined) setEmail(updates.email);
    if (filled.length > 0) addAiFields(filled);

    if (prefill.email) setEmail((current) => current || prefill.email!);
    if (prefill.linkedin) setLinkedin((current) => current || prefill.linkedin!);
  }, [scan.name, scan.company, scan.email, prefill.email, prefill.linkedin, addAiFields]);

  // ─── Cross-app / own-app reads. Same query keys as Home.tsx /
  // RepEventMapper.tsx (campaigns, sfdc-users) so react-query shares the
  // cache rather than firing a second request when both are mounted (the
  // wide master-detail layout renders Home and this component together). ──

  const campaignsQuery = useQuery({
    queryKey: LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
    queryFn: async () => {
      return fetchCampaigns(apiFetch);
    },
  });

  const sfdcUsersQuery = useQuery(sfdcUsersQueryOptions(apiFetch));

  const campaigns = campaignsQuery.data ?? [];
  const campaignsById = useMemo(() => new Map(campaigns.map((c) => [c.id, c])), [campaigns]);
  const reps = sfdcUsersQuery.data ?? [];
  const repsByEmail = useMemo(() => new Map(reps.map((r) => [r.email, r])), [reps]);

  const currentCampaign = campaignsById.get(campaignId) ?? null;
  const currentRep = repsByEmail.get(scannedByEmail) ?? null;
  const currentUserEmail = user?.ownerEmail ?? user?.email ?? null;

  const trimmedCampaignQuery = campaignQuery.trim().toLowerCase();
  const filteredCampaigns = campaigns
    .filter((c) => !trimmedCampaignQuery || `${c.name} ${c.city ?? ''}`.toLowerCase().includes(trimmedCampaignQuery))
    .sort((a, b) => (a.eventDate ?? '').localeCompare(b.eventDate ?? ''));

  const trimmedRepQuery = repQuery.trim().toLowerCase();
  const filteredReps = reps.filter(
    (r) => !trimmedRepQuery || `${r.name} ${r.email} ${r.role ?? ''}`.toLowerCase().includes(trimmedRepQuery),
  );

  // ─── LinkedIn enrichment (Clay) — the "[Rep] Find Email from LinkedIn"
  // routine (`enrich-clay.ts`'s file header has the live-verified contract).
  // The AUTO-fire path moved out of this component entirely
  // (`EnrichmentManager.tsx`, mounted once at the app root so it survives
  // navigation away from this scan) — `applyEnrichment` below is now used
  // ONLY by the manual "Enrich with Clay" button's mutation further down
  // this file; the auto-fire result reaching an already-open form is handled
  // by the Fix 5 effect above instead (it reads the manager's PATCH off the
  // live `scan` prop, not a query this component owns). `applyEnrichment`
  // never writes back to `linkedin` either way — that field is the routine's
  // INPUT, never overwritten by its response. ─────────────────────────────

  // For each of name/company/email, only apply a NON-EMPTY routine value —
  // a blank response must never clobber whatever the rep or another intake
  // path already put on the form. A non-empty value ALWAYS overwrites on a
  // manual re-run (`mode: 'manual'` — the rep explicitly asked for it,
  // mirrors `handleLinkMember`'s existing overwrite stance below). On the
  // AUTO-FIRE path (`mode: 'auto'`) a non-empty value only overwrites a field
  // that is STILL blank at apply time (Fix 2) — the request can take up to
  // 60s and the form stays editable while it's in flight (see the "Enrich
  // with Clay" button's own comment further down), so a mount-time-blank
  // check alone would let a same-tick-or-later rep edit get clobbered when
  // the result lands. `computeEnrichmentUpdates` above is the shared pure
  // core that decides this; every field this call actually filled gets
  // AI-highlighted, same mechanism a photo extraction uses.
  const applyEnrichment = useCallback(
    (result: EnrichLinkedInResponse, mode: 'auto' | 'manual') => {
      const { updates, filled } = computeEnrichmentUpdates(result, { name, company, email }, mode);
      if (updates.name !== undefined) setName(updates.name);
      if (updates.company !== undefined) setCompany(updates.company);
      if (updates.email !== undefined) setEmail(updates.email);
      addAiFields(filled);
      // Persist immediately (fire-and-forget) — otherwise the name/company
      // enrichment fills only live in this mounted form's React state until
      // the rep hits Submit, so Home's "Scans" card (and any other scan
      // list) keeps showing "Untitled scan" (`scanTitle()`, `scan.ts`)
      // for a scan a rep already has a real name for. This is a plain
      // Terra-DB write via the existing `PATCH /scans/:id` route — it never
      // triggers a Clay routine call and never touches Salesforce, so it's
      // independent of the actual "Scan lead" submit action. Applies to
      // BOTH the auto-fire and manual "Enrich with Clay" paths, since both
      // call through this one function.
      if (Object.keys(updates).length > 0) {
        void scanApi.patchScan(scan.id, updates).catch((err) => {
          // Best-effort — a failed background persist must not interrupt the
          // rep's in-progress review; the real submit still sends the same
          // (now-enriched) form fields on its own next write.
          console.error('[lead-scanner] failed to persist enrichment fields', err);
        });
      }
    },
    [addAiFields, name, company, email, scanApi, scan.id],
  );

  // Read-only count of any enrichment fetch in flight for THIS scan's key
  // (auto-fire runs from `EnrichmentManager.tsx`, OR a manual run from an
  // earlier mount of this same form) — a pure `useIsFetching` read, not a
  // second observer/fetch of its own, so it can safely coexist with
  // `EnrichmentManager`'s `useQueries` entry sharing the identical key.
  // Replaces the old `autoEnrichQuery.isFetching` this component used to own
  // directly.
  const isAutoEnriching = useIsFetching({ queryKey: ['lead-scanner', 'enrich', scan.id] }) > 0;

  const enrichMutation = useMutation({
    // Shared prefix with the auto-fire queries' key (`['lead-scanner',
    // 'enrich', ...]`) — kept for consistency even though this form reads
    // `enrichMutation.isPending` directly rather than a shared counter.
    mutationKey: ['lead-scanner', 'enrich'],
    // Same suppress-and-own-presentation stance as `submitMutation` below —
    // this call site's error toast is bespoke, not the app-wide generic one.
    meta: { suppressErrorToast: true },
    mutationFn: () => scanApi.enrichLinkedIn(normalizeLinkedInInput(linkedin)),
    onSuccess: (data) => applyEnrichment(data, 'manual'),
    onError: (err) => {
      addApiErrorToast({ content: "Couldn't enrich with Clay. Try again.", error: err });
    },
  });

  // ─── Submit / Retry / Resend — all three are the SAME call with the SAME
  // idempotency key (R12); only the `notes` payload varies. ────────────────

  function buildSubmitPayload(notesValue: string, forceResubmit: boolean, suppressFollowUpOverride?: boolean) {
    return {
      idempotencyKey: scan.idempotencyKey,
      sourceType: scan.sourceType,
      name,
      company,
      email,
      // Defensive re-normalization (U6, R14): the field's own `onBlur`
      // already normalizes on the common path, but this covers submits
      // that never blurred the field (e.g. a value set by AI extraction or
      // a member match that was never touched before Submit).
      linkedin: normalizeLinkedInInput(linkedin),
      notes: notesValue,
      routeToGtme,
      // "Add note & resend" (EnrichedInfoSection) carries its own live toggle
      // state, seeded from — but independently editable from — this form's
      // own `suppressFollowUp`; a plain Submit/Retry has no override to pass
      // and falls back to this form's live state.
      suppressFollowUp: suppressFollowUpOverride ?? suppressFollowUp,
      campaignId,
      scannedByEmail,
      matchedContactId,
      // Sent as `true` for the "Update in Salesforce" click AND for "Add note
      // & resend" (Fix 1) — without it, a resend against an already-`synced`
      // row hits `api.ts`'s synced-row short-circuit: the note lands on
      // Terra's own row via the upsert, but the routine never re-fires, so it
      // never reaches Clay/Salesforce, and the client re-polls the OLD
      // execution and reports a false "Lead submitted" success. Harmless for
      // every other status: `needs_review`/`error` rows (Retry's territory)
      // are already claimable through the default allow-list, so forcing it
      // there changes nothing. Retry itself still never sets this.
      ...(forceResubmit ? { forceResubmit: true } : {}),
    };
  }

  const submitMutation = useMutation({
    // This call site owns its own failure presentation (the response
    // modal / an error toast below), not the app-wide generic toast — see
    // mini-apps/CLAUDE.md's `useApi`/`meta.suppressErrorToast` contract.
    meta: { suppressErrorToast: true },
    mutationFn: async (vars: { notes: string; forceResubmit?: boolean; suppressFollowUpOverride?: boolean }) =>
      scanApi.submitScan(
        scan.id,
        buildSubmitPayload(vars.notes, vars.forceResubmit ?? false, vars.suppressFollowUpOverride),
      ),
    onSuccess: (data, vars) => {
      setNotes(vars.notes);
      // The submit response already carries the freshly-claimed `submitting`
      // row, so put it where this component's `scan` prop actually comes from
      // (the scans-list cache) instead of waiting for that list's own poll.
      // Without this, a Retry on an already-terminal row leaves `scan.status`
      // reading `needs_review`/`synced` for a beat — and
      // `resolveEnrichedInfoState`'s stale-pending carve-out reads that
      // combination as "trust the persisted row", re-rendering the PREVIOUS
      // run's result as authoritative right after the rep fired a new one.
      seedScanIntoScansCaches(queryClient, data.scan);
      // The submit call only FIRES the run (U9): it has no outcome to report,
      // so the "Enriched info" section shows its pending arm immediately and
      // the poll below fills the real result in.
      setFiredSeed({ pending: true });
      // Retry / "Add note & resend" fire a BRAND NEW run (a fresh server-side
      // claim cycle) against a per-SCAN poll key, so the previous execution's
      // cached result — and its still-in-flight request — both have to go.
      // `useScanApi.ts` owns the how and the why (both jobs are pinned by
      // `__tests__/submit-cache-seeding.test.ts`); firing it HERE rather than
      // in `onMutate` is the deliberate half that belongs to this call site.
      resetSubmitStatusForRetry(queryClient, scan.id);
    },
    onError: (err) => {
      // A genuine transport/protocol failure — a network error, a malformed
      // response, or the 409 the route answers when this scan is already mid-
      // run or already in Salesforce. A run that FAILS is not this path: it
      // fires fine and reports `status: 'failed'` through the poll, which the
      // modal renders as its failure layout.
      addApiErrorToast({ content: "Couldn't reach the submit endpoint. Try again.", error: err });
    },
  });

  // ─── The submit-status poll (U10). `enabled` is "a submission was fired
  // from THIS mounted form and hasn't resolved yet" — the seed is what marks
  // a run as fired (unchanged from the old modal's contract; only WHERE the
  // result renders moved, not this gate). The STOP-when-terminal half lives
  // in the hook's own `refetchInterval` (it returns `false` once an outcome
  // is in hand) rather than in this gate, which would otherwise have to read
  // the very query it configures. There is no client-side ceiling on
  // purpose — a slow run keeps reporting pending and the rep can navigate
  // away, because this route's own loopback reconcile is what finalizes the
  // run server-side.
  //
  // Nothing here is an effect or a ref, so there is nothing for StrictMode's
  // setup→cleanup→setup to strand: react-query owns the timer
  // (docs/v4-system/strictmode-resilient-effects.md). ──────────────────────
  const submitStatusQuery = useSubmitStatusQuery(scan.id, firedSeed?.pending === true);

  // The poll's answer wins the moment it exists; until then the rep sees the
  // pending seed. Both arms are the same `ScanSubmitResult` union, so the
  // section reads ONE shape and never has to know which produced it. `null`
  // means no submission has been fired from this mounted form at all.
  const displayedResult = submitStatusQuery.data?.result ?? firedSeed;

  // The single decision of what (if anything) the "Enriched info" section
  // shows — a live in-page result, a synthetic pending arm for a scan
  // reopened mid-flight, the scan's own persisted result for a scan reopened
  // later with nothing in flight, or nothing at all (`scan-review-logic.ts`).
  const enrichedInfo = resolveEnrichedInfoState(scan, displayedResult);

  function handleSubmitClick() {
    const errors = validateRequiredScanFields({ name, company });
    setFieldErrors(errors);
    if (hasRequiredFieldErrors(errors)) return;
    // "Update in Salesforce" (a `synced` row) is the only case that needs
    // `forceResubmit` — every other status already claims fine through the
    // default allow-list.
    submitMutation.mutate({ notes, forceResubmit: scan.status === 'synced' });
  }

  function handleRetry() {
    submitMutation.mutate({ notes });
  }

  function handleResend(note: string, suppressFollowUpValue: boolean) {
    const appended = note.trim() ? [notes, note.trim()].filter(Boolean).join('\n\n') : notes;
    // Fix 1: `forceResubmit: true` — see `buildSubmitPayload`'s comment above
    // for why this is required against an already-`synced` row.
    submitMutation.mutate({ notes: appended, forceResubmit: true, suppressFollowUpOverride: suppressFollowUpValue });
  }

  function handleLinkMember(member: CampaignMember) {
    setMatchedMember(member);
    // Prototype's `linkMember()` (~line 1305): replaces name/company/email,
    // preserves linkedin/notes/connect. A member match is a real matched
    // record, not an LLM guess, so it also clears any AI highlight on the
    // fields it overwrites. `memberPrefillFields` (`campaign-members.ts`) is
    // the single source of this field-mapping — shared with Home's
    // `handleMemberPick` so the two hosts can never silently diverge.
    const prefill = memberPrefillFields(member);
    setMatchedContactId(prefill.matchedContactId);
    setName(prefill.name);
    setCompany(prefill.company);
    setEmail(prefill.email);
    clearAiField('name');
    clearAiField('company');
    clearAiField('email');
  }

  function handleUnlinkMember() {
    setMatchedMember(null);
    setMatchedContactId(null);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <Heading size="xs">
          {scanTitle({ name, status: scan.status, sfdcError: scan.sfdcError })}
        </Heading>
        <div className="flex items-center gap-2">
          {/* Only for a scan reopened later that already has a persisted
              result and nothing currently in flight (`enrichedInfo.isPersisted`,
              `scan-review-logic.ts`) — a live in-page result needs no jump
              button, the rep is already looking at the page it's on. */}
          {enrichedInfo.isPersisted && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => enrichedInfoRef.current?.scrollIntoView({ behavior: 'smooth' })}
            >
              View results
            </Button>
          )}
          {/* The shared status maps, not a local three-arm ternary: a row
              that is mid-run now has its own `submitting` status (U9) and
              must read "Syncing" here, not "Draft". */}
          <StatusBadge status={SCAN_STATUS_BADGE[scan.status]} size="xs">
            {SCAN_STATUS_LABEL[scan.status]}
          </StatusBadge>
        </div>
      </div>

      {scan.status === 'error' && scan.sfdcError && (
        <Alert status="error" disableErrorLogging>
          <AlertDescription>
            {/* Submit-resilience fix (plan §3.4 item 9): a throttled START
                means Retry itself is the right next step, not hand-filling
                the form — the "fill the fields in by hand" suffix is wrong
                advice here and is skipped for exactly this message. */}
            {scan.sfdcError === SUBMIT_THROTTLED_MESSAGE
              ? scan.sfdcError
              : `${scan.sfdcError} — fill the fields in by hand.`}
          </AlertDescription>
        </Alert>
      )}

      {/* A normal `draft` row whose extraction found no legible badge/name
          tag/business card — advisory only, never blocking. Not an `error`
          (see `scanPatchForExtraction` in `extraction.ts`): every normal
          draft affordance still applies, the rep just gets a soft heads-up
          to fill the form in by hand. Self-contained copy, no appended
          instructions. */}
      {scan.status === 'draft' && scan.sfdcError === PARTIAL_DETECTION_MESSAGE && (
        <Alert status="warning">
          <AlertDescription>{PARTIAL_DETECTION_MESSAGE}</AlertDescription>
        </Alert>
      )}

      {/* needs_review (the two-outcome collapse fix): a partial/failed run
          landed, so the lead may or may not be fully in Salesforce. Never
          alarming — the routine may have gotten most of the way there — and
          the fields above are already pre-filled from whatever it did find. */}
      {scan.status === 'needs_review' && (
        <Alert status="warning">
          <AlertDescription>
            {scan.sfdcError
              ? `${scan.sfdcError} — review the details below and resubmit.`
              : "This lead didn't fully sync. We pre-filled what was found — review and resubmit."}
          </AlertDescription>
        </Alert>
      )}

      {/* "View Image" — the scanned photo, rendered inline once expanded
          (OCR/badge-extraction upload path only; the QR-scan and
          live-camera legs never persist bytes, so `scan.hasPhoto` is false
          for those). Controlled `open` (not `defaultOpen`) so expanding is
          what triggers `photoQuery`'s fetch — collapsing again doesn't
          re-fetch, `staleTime: Infinity` above keeps the blob cached for
          the life of this mount. */}
      {scan.hasPhoto && (
        <CollapsibleCard open={photoAccordionOpen} onOpenChange={setPhotoAccordionOpen}>
          <CollapsibleCardHeader>View Image</CollapsibleCardHeader>
          <CollapsibleCardBody className="p-0 flex flex-col gap-2">
            {photoUrl !== null ? (
              <Image
                src={photoUrl}
                alt="The photo taken for this scan"
                // `h-auto` sizes to the photo's own aspect ratio at full
                // width — no artificial letterbox padding for a normal
                // photo. `max-h` only kicks in for a tall portrait shot
                // that would otherwise push the rest of the form (and the
                // Submit button) off screen on a small phone (iPhone 13
                // mini: 375×812); `object-contain` keeps it undistorted
                // when that cap does engage.
                className="block h-auto max-h-[60vh] w-full object-contain"
              />
            ) : (
              <div className="p-4">
                <Text size="sm" variant="tertiary">
                  {photoQuery.isError ? "Couldn't load this photo." : 'Loading photo…'}
                </Text>
              </div>
            )}
            {scan.photoFilename && (
              <Text size="xs" variant="tertiary" className="px-4 pb-4">
                {scan.photoFilename}
              </Text>
            )}
          </CollapsibleCardBody>
        </CollapsibleCard>
      )}

      {/* ── Form tail: Filed under (prototype's formTail(), ~line 1342) —
          moved above the member-search/form block and collapsed by default
          so the rep sees the campaign/rep attribution summary at a glance
          without it crowding the form; expanding reveals the same two
          search-to-change comboboxes. Uncontrolled `defaultOpen` is
          intentional: `ScanReview` is remounted per-scan (`key={scan.id}`
          in ViewSwitcher.tsx), so this resets to closed on its own without
          a reset effect. ── */}
      <CollapsibleCard defaultOpen={false}>
        <CollapsibleCardHeader>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Text size="sm" weight="semibold">
              Filed under
            </Text>

            <div className="flex items-center justify-between gap-2">
              <Text size="sm">{currentCampaign ? currentCampaign.name : '— none selected —'}</Text>
              <StatusBadge status={campaignId === initialCampaignId ? 'neutral' : 'action'} size="xs">
                {campaignId === initialCampaignId ? 'auto' : 'changed'}
              </StatusBadge>
            </div>

            <div className="flex items-center justify-between gap-2">
              <Text size="sm">
                {currentRep ? currentRep.name : scannedByEmail}
                {scannedByEmail === currentUserEmail && (
                  <StatusBadge status="neutral" size="xs" className="ml-1.5">
                    you
                  </StatusBadge>
                )}
              </Text>
              <StatusBadge status={scannedByEmail === initialScannedByEmail ? 'neutral' : 'action'} size="xs">
                {scannedByEmail === initialScannedByEmail ? 'auto' : 'changed'}
              </StatusBadge>
            </div>
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <ComboboxField
              variant="popover"
              aria-label="Search events to change"
              placeholder="Search events to change…"
              searchPlaceholder="Search events…"
              items={filteredCampaigns}
              itemToStringLabel={(c: Campaign) => c.name}
              value={currentCampaign}
              onValueChange={(c) => setCampaignId(c ? c.id : initialCampaignId)}
              inputValue={campaignQuery}
              onInputValueChange={setCampaignQuery}
              by="id"
            >
              {filteredCampaigns.map((c) => (
                <ComboboxOption key={c.id} value={c}>
                  {c.name} — {campaignWhenAndWhere(c)}
                </ComboboxOption>
              ))}
            </ComboboxField>
            <Text size="xs" variant="tertiary">
              Change this if the lead belongs to a different event.
            </Text>
          </div>

          <div className="flex flex-col gap-1.5">
            <ComboboxField
              variant="popover"
              aria-label="Search people to change"
              placeholder="Search people to change…"
              searchPlaceholder="Search people…"
              items={filteredReps}
              itemToStringLabel={(r: Rep) => r.name}
              value={currentRep}
              onValueChange={(r) => setScannedByEmail(r ? r.email : initialScannedByEmail)}
              inputValue={repQuery}
              onInputValueChange={setRepQuery}
              by="email"
            >
              {filteredReps.map((r) => (
                <ComboboxOption key={r.email} value={r}>
                  {r.name} — {[r.role, r.email].filter(Boolean).join(' · ')}
                </ComboboxOption>
              ))}
            </ComboboxField>
            <Text size="xs" variant="tertiary">
              Change this if you scanned on someone else's behalf.
            </Text>
          </div>
        </CollapsibleCardBody>
      </CollapsibleCard>

      <Card padded className="flex flex-col gap-4">
        {/* Auto-fire (`EnrichmentManager.tsx`, survives navigation) or the
            manual "Enrich with Clay" button below — same visual either way,
            rendered right at the top of THIS scan's own form rather than as
            a page-spanning bar: it was previously mounted above the whole
            app shell (`index.tsx`'s `EnrichmentBar`), which floated it above
            the app's own header/nav and made it easy to miss or scroll past
            entirely. `isAutoEnriching` is scoped to `scan.id`'s query key, so
            this only lights up for a run against the scan actually open
            here. */}
        {(isAutoEnriching || enrichMutation.isPending) && <EnrichingWithClay />}

        {/* ── Optional existing-campaign-member typeahead ── */}
        <CampaignMemberSearch
          campaignId={campaignId}
          linkedMember={matchedMember}
          onLink={handleLinkMember}
          onUnlink={handleUnlinkMember}
        />

        {/* ── The form itself (prototype's scanForm()) ── */}
        <Fieldset className="flex flex-col gap-3">
          <InputField
            label="Full name *"
            isAIHighlighted={aiFields.has('name')}
            placeholder="Jane Doe"
            value={name}
            onValueChange={(v) => {
              setName(v);
              clearAiField('name');
              if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: null }));
            }}
            invalid={fieldErrors.name != null}
            errorMessage={fieldErrors.name}
          />
          <InputField
            label="Company *"
            isAIHighlighted={aiFields.has('company')}
            placeholder="Acme Inc"
            value={company}
            onValueChange={(v) => {
              setCompany(v);
              clearAiField('company');
              if (fieldErrors.company) setFieldErrors((prev) => ({ ...prev, company: null }));
            }}
            invalid={fieldErrors.company != null}
            errorMessage={fieldErrors.company}
          />
          <InputField
            label="Email"
            description="Optional"
            isAIHighlighted={aiFields.has('email')}
            type="email"
            placeholder="jane@acme.com"
            value={email}
            onValueChange={(v) => {
              setEmail(v);
              clearAiField('email');
            }}
          />
          <InputField
            label="LinkedIn"
            description="Optional"
            isAIHighlighted={aiFields.has('linkedin')}
            placeholder="linkedin.com/in/…"
            value={linkedin}
            onValueChange={(v) => {
              setLinkedin(v);
              clearAiField('linkedin');
            }}
            onBlur={() => setLinkedin((current) => normalizeLinkedInInput(current))}
          />
          {/* QA-fix batch, Fix 5 — a small, unobtrusive link to the
              LinkedIn-enrichment Clay function, next to (not competing
              with) the LinkedIn input itself. */}
          <TextLink
            href="https://app.clay.com/workspaces/859539/tables/t_0tb2t33MwuDPSQiQhpG/views/gv_0tb2t34U86XyJYGy89q"
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            size="xs"
          >
            View function
          </TextLink>
          {/* R14 — no maxLength anywhere on this field, and none will be
              added: `notes` is full verbatim source text (settled decision). */}
          <TextareaField
            label="Notes"
            placeholder="What did you talk about?"
            value={notes}
            onValueChange={setNotes}
          />
          <SwitchField
            label="Force route to GTME"
            description="Off by default — turn it on to force route this lead to GTME."
            checked={routeToGtme}
            onCheckedChange={setRouteToGtme}
          />
          <SwitchField
            label="Suppress from Follow Up"
            description="I will follow up or lead booked, suppress from marketing follow up"
            checked={suppressFollowUp}
            onCheckedChange={setSuppressFollowUp}
          />
        </Fieldset>
      </Card>

      {/* "Enrich with Clay" — the MANUAL trigger for the same routine
          `EnrichmentManager.tsx` can auto-fire in the background. Visible
          whenever the LIVE form value has a LinkedIn URL (not `scan.linkedin`,
          which never changes for this mount) — always available for a re-run,
          never hidden once fields are filled, so a rep can retry after
          editing the LinkedIn field by hand. `variant="secondary"` — Submit
          stays the one primary action below. */}
      {linkedin.trim() !== '' && (
        <Button
          variant="secondary"
          size="lg"
          loading={enrichMutation.isPending}
          disabled={enrichMutation.isPending || isAutoEnriching || scan.status === 'submitting'}
          onClick={() => enrichMutation.mutate()}
        >
          Enrich with Clay
        </Button>
      )}

      {/* A row already mid-run can't be submitted again — the server's claim
          (U9 step 3) answers 409, so offering the button would only produce a
          failed request. The "Syncing" badge above is the explanation. A
          `synced` row IS submittable — that's the resubmit case below (label
          only — behavior is unchanged, `forceResubmit`). */}
      <Button
        variant="primary"
        size="lg"
        loading={submitMutation.isPending}
        disabled={scan.status === 'submitting'}
        onClick={handleSubmitClick}
      >
        {scan.status === 'synced' ? 'Scan lead' : 'Submit lead'}
      </Button>

      {/* "Enriched info" (product-owner simplification of the old
          `SubmitResponseModal` popup) — in-page, not an overlay. `null`
          means `resolveEnrichedInfoState` decided there's nothing to show
          yet (a fresh draft/error scan with no submission history). */}
      {enrichedInfo.result && (
        <EnrichedInfoSection
          containerRef={enrichedInfoRef}
          scan={{ name, company, suppressFollowUp }}
          result={enrichedInfo.result}
          campaignName={currentCampaign?.name ?? '—'}
          scannedByName={currentRep?.name ?? scannedByEmail}
          onRetry={handleRetry}
          onResend={handleResend}
          onKeepAsDraft={() => setFiredSeed(null)}
          isSubmitting={submitMutation.isPending}
        />
      )}
    </div>
  );
}
